// components/minimal/MinimalDialog.tsx
//
// Drop-in replacement for MUI's Dialog with motion that matches
// the rest of the minimal-theme surfaces: a quick scale-up +
// fade-in on open, fade-out on close. Spring-based so it feels
// crisp, not floaty.
//
// Three exports:
//
//   - MinimalDialog            — the wrapper. Same props as MUI's
//                                 Dialog (open, onClose, children,
//                                 maxWidth, fullWidth). Extra
//                                 surface styling: flat dark, no
//                                 glass/blur.
//
//   - MinimalDialogActions     — pre-styled action button row.
//                                 Sits at the bottom, right-aligned,
//                                 with the cyan primary action.
//
//   - MinimalConfirmDialog     — common confirm-destroy / confirm-
//                                 commit pattern. Wraps MinimalDialog
//                                 with title + body + Cancel/Confirm
//                                 buttons. Saves callers from
//                                 building it inline every time.
//
// Why not lean on MUI's Dialog transition prop? Because MUI's
// Transition component-API is built around the old TransitionGroup
// world; getting Framer Motion's springs through it requires
// adapter code that ends up uglier than just owning the modal
// rendering ourselves. The trade-off: we re-implement focus trap
// + scroll lock, which Dialog gave for free. Inside MinimalDialog
// we keep MUI's Dialog as the backbone so we get those for free
// while replacing only the visual + motion shell.

import { ReactElement, ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
} from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';

const SURFACE_BG = '#161A24';
const BORDER = 'rgba(255, 255, 255, 0.06)';
const ACCENT = '#00FFD4';
const MUTED = '#8892A0';
const DANGER = '#FF6B35';

export interface MinimalDialogProps {
  open: boolean;
  onClose: () => void;
  /** Page-style title at the top of the dialog. Pass null/omit if
   *  you want a chrome-less dialog (e.g. for a media viewer). */
  title?: ReactNode;
  children: ReactNode;
  /** Same MUI maxWidth tokens — keeps the API familiar. */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  /** Stretches the dialog to maxWidth (vs. shrinking to content). */
  fullWidth?: boolean;
  /** Backdrop-click + ESC closing. Defaults true. Pass false for
   *  forms-in-progress where accidental dismiss would lose data. */
  disableBackdropClose?: boolean;
}

export function MinimalDialog({
  open,
  onClose,
  title,
  children,
  maxWidth = 'sm',
  fullWidth = true,
  disableBackdropClose = false,
}: MinimalDialogProps): ReactElement {
  return (
    <AnimatePresence>
      {open && (
        <Dialog
          open={open}
          onClose={(_, reason) => {
            if (disableBackdropClose && reason === 'backdropClick') return;
            onClose();
          }}
          maxWidth={maxWidth}
          fullWidth={fullWidth}
          // Replace MUI's default Fade transition with our own
          // motion. Setting TransitionComponent=Fragment-like
          // effectively turns it off; we then mount motion.div
          // inside.
          TransitionProps={{ timeout: 0 }}
          PaperProps={{
            sx: {
              backgroundColor: 'transparent',
              boxShadow: 'none',
              overflow: 'visible',
              backgroundImage: 'none',
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: 'rgba(8, 10, 16, 0.65)',
              backdropFilter: 'blur(3px)',
            },
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 4 }}
            transition={{ type: 'spring', stiffness: 360, damping: 28 }}
            style={{ width: '100%' }}
          >
            <Box
              sx={{
                backgroundColor: SURFACE_BG,
                border: `1px solid ${BORDER}`,
                borderRadius: '14px',
                color: '#FFFFFF',
                overflow: 'hidden',
              }}
            >
              {title && (
                <DialogTitle
                  sx={{
                    color: '#FFFFFF',
                    fontFamily: '"Rajdhani", sans-serif',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    letterSpacing: '-0.01em',
                    borderBottom: `1px solid ${BORDER}`,
                    py: 2,
                  }}
                >
                  {title}
                </DialogTitle>
              )}
              <DialogContent sx={{ py: 2.5 }}>{children}</DialogContent>
            </Box>
          </motion.div>
        </Dialog>
      )}
    </AnimatePresence>
  );
}

/**
 * Pre-styled action row for the bottom of a MinimalDialog. Right-
 * aligned, gap between buttons. Use motion.button children directly
 * or pass through MUI Button — both work, the row just lays out.
 */
export function MinimalDialogActions({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 1,
        px: 3,
        py: 2,
        borderTop: `1px solid ${BORDER}`,
        backgroundColor: 'rgba(255, 255, 255, 0.015)',
      }}
    >
      {children}
    </Box>
  );
}

export interface MinimalConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  /** Body text. Pass a ReactNode for more flexibility (links,
   *  bold, etc.). */
  message: ReactNode;
  /** Defaults: 'Cancel' / 'Confirm'. Override for clarity (e.g.
   *  'Remove' for destroy actions). */
  cancelLabel?: string;
  confirmLabel?: string;
  /** 'danger' uses orange for the confirm button (destructive
   *  acts). 'primary' uses cyan (commit / accept). */
  tone?: 'primary' | 'danger';
  /** Disables the confirm button while the action is in flight. */
  busy?: boolean;
}

/**
 * Common confirm-this-action pattern. Saves you ~30 lines per
 * caller. The Confirm button picks its colour from the tone prop:
 * cyan for normal, orange for destructive.
 */
export function MinimalConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  cancelLabel = 'Cancel',
  confirmLabel = 'Confirm',
  tone = 'primary',
  busy = false,
}: MinimalConfirmDialogProps): ReactElement {
  const confirmColor = tone === 'danger' ? DANGER : ACCENT;
  return (
    <MinimalDialog
      open={open}
      onClose={onClose}
      title={title}
      maxWidth="xs"
    >
      <Typography sx={{ color: '#C9D1D9', fontSize: '0.95rem' }}>
        {message}
      </Typography>
      <Box sx={{ height: 8 }} />
      <MinimalDialogActions>
        <motion.button
          whileHover={{ y: -1 }}
          whileTap={{ scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 500, damping: 26 }}
          onClick={onClose}
          disabled={busy}
          style={{
            padding: '6px 14px',
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            color: MUTED,
            border: '1px solid rgba(255, 255, 255, 0.06)',
            borderRadius: 6,
            fontFamily: 'inherit',
            fontWeight: 500,
            fontSize: '0.85rem',
            cursor: busy ? 'default' : 'pointer',
          }}
        >
          {cancelLabel}
        </motion.button>
        <motion.button
          whileHover={busy ? undefined : { y: -1 }}
          whileTap={busy ? undefined : { scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 500, damping: 26 }}
          onClick={onConfirm}
          disabled={busy}
          style={{
            padding: '6px 14px',
            backgroundColor: confirmColor,
            color: '#0E1118',
            border: 'none',
            borderRadius: 6,
            fontFamily: 'inherit',
            fontWeight: 700,
            fontSize: '0.85rem',
            cursor: busy ? 'default' : 'pointer',
            opacity: busy ? 0.6 : 1,
          }}
        >
          {busy ? 'Working…' : confirmLabel}
        </motion.button>
      </MinimalDialogActions>
    </MinimalDialog>
  );
}

export default MinimalDialog;
